import { Button, Form, withField, Toast } from '@douyinfe/semi-ui';
import { useHistory, useParams } from '@jupiter/plugin-runtime/router';
import styled from '@jupiter/plugin-runtime/styled';
import { SylApi } from '@syllepsis/adapter';

import { useEffect, useRef } from 'react';
import { FormApi } from '@douyinfe/semi-ui/lib/es/form';
import { useRequest } from '@byted/hooks';
import EditorV2 from '@/components/EditorV2';
import useBiz from '@/context/useBiz';
import { forumPostService } from '@/api/forumPost';
import { forumpost } from '@/typings/open.platform.forum_bff';
import { toastError } from '@/utils/toastError';
import { usePost } from '@/hooks/usePost';
import { useScope } from '@/hooks/useScope';
import usePostImageSet from '@/hooks/usePostImageSet';

function useUpdatePost() {
  const history = useHistory();
  const scope = useScope();
  const { loading, run } = useRequest(
    async (req: forumpost.UpdatePostRequest) => {
      try {
        const res = await forumPostService.updatePost(req);
        if (res.error) {
          throw new Error(res.message);
        }
        Toast.success('操作成功, 3秒后跳转到帖子详情页');
        setTimeout(() => {
          history.push(`/${scope}/post/${req.PostId}`);
        }, 3000);
      } catch (e) {
        toastError(e);
      }
    },
  );

  return {
    updatingPost: loading,
    updatePost: run,
  };
}

export const TitleInput = withField(
  ({
    value = '',
    onChange,
  }: {
    value?: string;
    onChange?: (value: string) => void;
  }) => (
    <TitleBox>
      <CustomInput
        name="title"
        value={value}
        placeholder="请输入标题(不超过40个字)"
        onChange={event => onChange?.(event.target.value)}
      />
      <TitleCount
        style={{
          color: value.length > 40 || value.length === 0 ? '#f44336' : '#999',
        }}>
        {value.length}/40
      </TitleCount>
    </TitleBox>
  ),
);

const TitleBox = styled.div`
  display: flex;
  align-items: center;
`;

const CustomInput = styled.input`
  font-size: 2em;
  flex: 1;
  font-weight: 700;
  line-height: 73px;
  display: block;
  margin: 0;
  border: none;
  &::placeholder {
    color: #cccccc;
    font-weight: 400;
  }
`;

const TitleCount = styled.span`
  font-size: 16px;
  font-family: monospace;
`;

const Edit: React.FC = () => {
  const { replaceUrl, getExitUris } = usePostImageSet();

  const { postId } = useParams<{
    postId: string;
  }>();
  const editorRef = useRef<SylApi>();
  const formRef = useRef<FormApi>();
  const history = useHistory();
  const { postDetail, fetchPost } = usePost();
  const biz = useBiz(s => s.biz);
  const { updatePost, updatingPost } = useUpdatePost();

  useEffect(() => {
    if (!biz || !postId) {
      return;
    }
    fetchPost({
      Biz: biz,
      PostId: postId,
    });
  }, [biz, postId]);

  useEffect(() => {
    if (!formRef.current) {
      return;
    }
    formRef.current.setValue('title', postDetail?.PostInfo?.Title ?? '');
    editorRef.current?.setHTML(postDetail?.PostInfo?.Html ?? '');
  }, [postDetail?.PostInfo.Title]);
  return (
    <div className="max-w-[996px] bg-white mx-auto px-8 py-6 rounded-xl mt-4 mb-6 shadow">
      <Form
        getFormApi={api => {
          formRef.current = api;
        }}>
        <Form.Slot>
          <TitleInput
            field="title"
            noLabel={true}
            initValue={postDetail?.PostInfo?.Title ?? ''}
            rules={[
              {
                validator(_rule, value = '', cb) {
                  if (value.length === 0) {
                    cb('标题不能为空');
                    return false;
                  }
                  if (value.length > 40) {
                    cb('标题长度不能大于40');
                    return false;
                  }
                  cb('');
                  return true;
                },
              },
            ]}
          />
        </Form.Slot>
        <EditorV2
          getEditorInstance={ins => {
            editorRef.current = ins;
          }}
          defaultValue=""
        />
        <div className="flex justify-center mt-4">
          <div className="space-x-6">
            <Button
              type="primary"
              theme="solid"
              loading={updatingPost}
              onClick={() => {
                const html = editorRef.current?.getHTML();
                if (!html) {
                  toastError('帖子内容不能为空');
                  return;
                }
                if (!formRef.current || !postDetail?.PostInfo?.Id) {
                  return;
                }
                const postContent = replaceUrl(html);

                updatePost({
                  PostId: postDetail.PostInfo.Id,
                  Html: postContent,
                  Title: formRef.current.getValue('title'),
                  Uid: '',
                  Biz: biz,
                  Uris: getExitUris(postContent),
                });
              }}>
              保存
            </Button>
            <Button
              onClick={() => {
                history.go(-1);
              }}>
              取消
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};
export default Edit;
